import { useQuery } from '@apollo/client';
import { DynamicResultTable, FormattedDate, NoDataContentInTable } from '@css-xp/base';
import {
  ChargeQuantity,
  ChargeQuantityUsersUpdate,
  FetchUserHistoryForEntitlementQuery,
  FetchUserHistoryForEntitlementQueryVariables,
  LinkedDestinationEntitlement,
} from '@css-xp/graphtypes/src/generated-types-new';
import { DATE_TIME_FORMAT } from '@atlassian/cssxp-typescommon';
import { DEFAULT_PAGE, DEFAULT_ROWS_PER_PAGE, NO_DETAILS_TO_SHOW } from '@atlassian/cssxp-utils-entitlement';
import memoize from 'lodash/memoize';
import React, { useMemo, useState } from 'react';
import { fetchUserHistoryForEntitlementQuery } from '../../queries/fetch-user-history-for-entitlement';
import { ListUtil } from '../utils/list';
import './user-history-table.scss';

interface Props {
  id: string;
  createdDate?: string | null;
  parentProductEntitlements: LinkedDestinationEntitlement[];
  parentEntitlementsLoading: boolean;
}

export const emptyStateOrderProp = {
  header: '',
  description: NO_DETAILS_TO_SHOW,
  imageUrl: '',
  testId: 'no-results-user-history-state',
};

export const UserHistoryTable = ({
  id,
  createdDate,
  parentProductEntitlements = [],
  parentEntitlementsLoading,
}: Props) => {
  const highestUsageParentEntitlement = useMemo(() => {
    return parentProductEntitlements.sort((a, b) => (Number(b?.activeUsers) ?? 0) - (Number(a?.activeUsers) ?? 0))?.[0];
  }, [parentProductEntitlements]);

  const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const { loading, data } = useQuery<FetchUserHistoryForEntitlementQuery, FetchUserHistoryForEntitlementQueryVariables>(
    fetchUserHistoryForEntitlementQuery,
    {
      variables: {
        id,
        filterOptions: [
          {
            key: 'pageSize',
            value: '100',
          },
          {
            key: 'start',
            value: createdDate,
          },
          {
            key: 'parentEntitlementId',
            value: highestUsageParentEntitlement?.entitlementId,
          },
        ],
      },
      skip: !id || parentEntitlementsLoading,
    }
  );

  const chargeQuantities = data?.entitlementDetails?.userHistory?.results ?? [];

  const list = new ListUtil(chargeQuantities);
  const defaultList = list.get({ first: rowsPerPage - 1, offset: currentPage - 1 }) as (ChargeQuantity & {
    id: string;
  })[];
  const [resultList, setResultList] = useState(defaultList);

  React.useEffect(() => {
    if (!loading) {
      setResultList(defaultList);
    }
  }, [loading]);

  const refetch = ({ offset, first }: { offset: number; first: number }) => {
    setResultList(list.get({ first, offset }) as (ChargeQuantity & { id: string })[]);
  };

  const onPageChange = (newPage: number) => {
    if (currentPage === newPage) {
      return;
    }
    setCurrentPage(newPage);

    refetch({
      offset: newPage !== 1 ? newPage * rowsPerPage - rowsPerPage : 0,
      first: rowsPerPage - 1,
    });
  };

  const onRowPerPageChange = (newRowPerPage: number) => {
    if (newRowPerPage === rowsPerPage) {
      return;
    }
    setRowsPerPage(newRowPerPage);
    refetch({
      offset: 0,
      first: newRowPerPage - 1,
    });
  };

  const getUserChangeString = (
    usersUpdate: ChargeQuantityUsersUpdate,
    isAddColumn: boolean,
    isRemoveColumn: boolean
  ) => {
    if (usersUpdate.isCreated && isAddColumn) {
      return `Created`;
    }
    if (usersUpdate.isAdded && isAddColumn) {
      return `+ ${usersUpdate.changeBy}`;
    }
    if (usersUpdate.isRemoved && isRemoveColumn) {
      return `- ${usersUpdate.changeBy}`;
    }
    return '';
  };

  const getRows = () => {
    return resultList.map((rowList) => {
      return {
        key: rowList.id,
        cells: [
          {
            content: rowList?.lastUpdatedAt ? (
              <FormattedDate date={rowList?.lastUpdatedAt} format={DATE_TIME_FORMAT} />
            ) : (
              <NoDataContentInTable />
            ),
          },
          {
            content: (
              <span className="user-change-add">
                {rowList?.usersUpdate ? (
                  getUserChangeString(rowList?.usersUpdate, true, false)
                ) : (
                  <NoDataContentInTable />
                )}
              </span>
            ),
          },
          {
            content: (
              <span className="user-change-remove">
                {rowList?.usersUpdate ? (
                  getUserChangeString(rowList?.usersUpdate, false, true)
                ) : (
                  <NoDataContentInTable />
                )}
              </span>
            ),
          },
          {
            content: rowList?.quantity?.toString(),
          },
        ],
      };
    });
  };

  const getHead = memoize(() => {
    return {
      cells: [
        {
          content: 'Date',
          width: 25,
        },
        {
          content: 'Users added',
          width: 25,
        },
        {
          content: 'Users removed',
          width: 25,
        },
        {
          content: 'Total users',
          width: 25,
        },
      ],
    };
  });

  return (
    <DynamicResultTable
      isLoading={loading}
      testId="user-history"
      headingText="User history"
      head={getHead()}
      rows={getRows()}
      emptyStateProps={emptyStateOrderProp}
      totalCount={list.totalCount}
      currentPage={currentPage}
      rowsPerPage={rowsPerPage}
      onPageChange={onPageChange}
      isPaginationEnabled={list.totalCount > DEFAULT_ROWS_PER_PAGE}
      onRowChange={onRowPerPageChange}
    />
  );
};
